import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, InformationCircleIcon, CheckIcon } from '@heroicons/react/24/outline';
import { authService } from '../../services/authService';
import campaignService, { Campaign } from '../../services/campaignService';

interface Segment {
  id: number;
  name: string;
  description: string;
  active_leads: number;
  total_leads: number;
}


interface CampaignCreatorProps {
  campaign?: any; // For editing existing campaigns
  onSave: (results: { campaigns_created: number; campaign_ids: number[] }) => void;
  onCancel: () => void;
}

const CampaignCreator: React.FC<CampaignCreatorProps> = ({ campaign, onSave, onCancel }) => {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(false);
  const [segmentsLoading, setSegmentsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSegments, setSelectedSegments] = useState<number[]>(campaign?.segment_id ? [campaign.segment_id] : []);
  const [previewMode, setPreviewMode] = useState<'preview' | 'source'>('preview');
  
  const [formData, setFormData] = useState({
    name: campaign?.name || '',
    subject: campaign?.subject || '',
    content_html: campaign?.content_html || '',
    content_text: campaign?.content_text || ''
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  const isEditing = !!campaign;

  // Load segments
  const loadSegments = useCallback(async () => {
    try {
      setSegmentsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/segments`, {
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to load segments');
      }

      const data = await response.json();
      setSegments(data.filter((segment: Segment) => segment.active_leads > 0));
    } catch (err) {
      console.error('Error loading segments:', err);
      setError('Failed to load segments');
    } finally {
      setSegmentsLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    loadSegments();
  }, [loadSegments]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSegmentToggle = (segmentId: number) => {
    setSelectedSegments(prev => 
      prev.includes(segmentId) 
        ? prev.filter(id => id !== segmentId)
        : [...prev, segmentId]
    );
  };

  const selectAllSegments = () => {
    setSelectedSegments(segments.map(s => s.id));
  };

  const clearAllSegments = () => {
    setSelectedSegments([]);
  };

  const generatePlainTextFromHtml = (html: string): string => {
    // Simple HTML to text conversion
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Extract body content from full HTML document or return as-is for fragments
  const extractBodyContent = (html: string): string => {
    // Check if it's a full HTML document with body tags
    if (html.includes('<body') || html.includes('<BODY')) {
      // Extract everything between body tags
      const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      if (bodyMatch && bodyMatch[1]) {
        return bodyMatch[1].trim();
      }
    }
    // If no body tags found, return the HTML as is
    return html;
  };

  // Extract styles from HTML document
  const extractStyles = (html: string): string => {
    const styles: string[] = [];
    
    // Extract style tags
    const styleMatches = html.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
    if (styleMatches) {
      styleMatches.forEach(match => {
        const styleContent = match.replace(/<\/?style[^>]*>/gi, '');
        styles.push(styleContent);
      });
    }
    
    return styles.join('\n');
  };

  const handleHtmlContentChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      content_html: value,
      content_text: generatePlainTextFromHtml(value)
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) return 'Campaign name is required';
    if (!formData.subject.trim()) return 'Subject line is required';
    if (!formData.content_html.trim()) return 'Campaign content is required';
    if (selectedSegments.length === 0) return 'Please select at least one segment';
    
    // When editing, only allow one segment selection
    if (isEditing && selectedSegments.length > 1) {
      return 'When editing a campaign, please select exactly one segment';
    }
    
    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditing) {
        // For editing, use the single selected segment
        let updateData: Partial<Campaign> = { 
          ...formData,
          segment_id: selectedSegments[0] // Use the first (and only) selected segment
        };
        
        // If editing a rejected campaign, reset it back to draft status
        if (campaign.approval_status === 'rejected') {
          updateData = {
            ...formData,
            segment_id: selectedSegments[0],
            approval_status: 'draft',
            approval_feedback: undefined,
            approved_at: undefined,
            approved_by_email: undefined,
            approval_token: undefined
          };
        }
        
        await campaignService.updateCampaign(campaign.id, updateData);
        onSave({ campaigns_created: 1, campaign_ids: [campaign.id] });
      } else {
        // For creating new campaigns, create one per selected segment
        const createdCampaigns = [];
        
        for (const segmentId of selectedSegments) {
          const result = await campaignService.createCampaign({
            name: selectedSegments.length > 1 
              ? `${formData.name} - ${segments.find(s => s.id === segmentId)?.name}`
              : formData.name,
            subject: formData.subject,
            content_html: formData.content_html,
            content_text: formData.content_text,
            segment_id: segmentId
          });
          createdCampaigns.push(result.id);
        }
        
        onSave({ 
          campaigns_created: createdCampaigns.length,
          campaign_ids: createdCampaigns
        });
      }
    } catch (err) {
      console.error('Error saving campaign:', err);
      setError(err instanceof Error ? err.message : 'Failed to save campaign');
    } finally {
      setLoading(false);
    }
  };

  // Calculate total recipients across selected segments
  const totalRecipients = selectedSegments.reduce((total, segmentId) => {
    const segment = segments.find(s => s.id === segmentId);
    return total + (segment?.active_leads || 0);
  }, 0);

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? (campaign.approval_status === 'rejected' ? 'Edit Rejected Campaign' : 'Edit Campaign') : 'Create New Campaign'}
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row h-[calc(95vh-140px)]">
          {/* Left Column - Form Fields */}
          <div className="flex-1 lg:w-1/2 p-6 overflow-y-auto border-r border-gray-200">
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-red-400 mr-2" />
                  <div className="text-sm text-red-700">{error}</div>
                </div>
              </div>
            )}

            {/* Show rejection feedback if editing a rejected campaign */}
            {isEditing && campaign.approval_status === 'rejected' && campaign.approval_feedback && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="text-sm font-medium text-red-800 mb-2">Revision Required</h4>
                <div className="text-sm text-red-700 mb-2">
                  <strong>Feedback from reviewer:</strong>
                </div>
                <div className="text-sm text-red-700 bg-white p-3 rounded border whitespace-pre-wrap">
                  {campaign.approval_feedback}
                </div>
                {campaign.approved_by_email && (
                  <div className="text-xs text-red-600 mt-2">
                    Rejected by: {campaign.approved_by_email}
                  </div>
                )}
                <div className="text-xs text-red-600 mt-2 flex items-center">
                  <CheckIcon className="h-3 w-3 mr-1" />
                  Making changes will reset this campaign to draft status for resubmission.
                </div>
              </div>
            )}

            <div className="space-y-6">
            {/* Campaign Name */}
            <div>
              <label htmlFor="campaign-name" className="block text-sm font-medium text-gray-700 mb-2">
                Campaign Name *
              </label>
              <input
                id="campaign-name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="e.g., Weekly Newsletter - January 2024"
              />
            </div>

            {/* Subject Line */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                Subject Line *
              </label>
              <input
                id="subject"
                type="text"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="e.g., Important updates for your child's education"
              />
            </div>

            {/* Target Segments */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  {isEditing 
                    ? `Select Target Segment (${selectedSegments.length} selected)` 
                    : `Select Segments (${selectedSegments.length} selected)`
                  }
                </label>
                {!isEditing && (
                  <div className="space-x-2">
                    <button
                      type="button"
                      onClick={selectAllSegments}
                      className="text-sm text-indigo-600 hover:text-indigo-800"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={clearAllSegments}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Clear All
                    </button>
                  </div>
                )}
              </div>

              {isEditing && (
                <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Editing Mode:</strong> Select exactly one segment for this campaign. To target multiple segments, create separate campaigns.
                  </p>
                </div>
              )}

              {segmentsLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                  <span className="text-sm text-gray-500">Loading segments...</span>
                </div>
              ) : segments.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex items-center">
                    <InformationCircleIcon className="h-5 w-5 text-yellow-400 mr-2" />
                    <div className="text-sm text-yellow-800">
                      No segments with active leads found. Create segments first to target specific audiences.
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {segments.map((segment) => (
                      <div
                        key={segment.id}
                        className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                          selectedSegments.includes(segment.id)
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => handleSegmentToggle(segment.id)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-gray-900">
                              {segment.name}
                            </h4>
                            <p className="text-sm text-gray-500 line-clamp-2 leading-tight mt-1" title={segment.description}>
                              {segment.description}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {segment.active_leads} active leads
                            </p>
                          </div>
                          {selectedSegments.includes(segment.id) && (
                            <CheckIcon className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {selectedSegments.length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>{totalRecipients}</strong> total recipients across{' '}
                        <strong>{selectedSegments.length}</strong> segments
                      </p>
                      {!isEditing && selectedSegments.length > 1 && (
                        <p className="text-sm text-blue-600 mt-1">
                          Will create {selectedSegments.length} separate campaigns (one per segment)
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Email Content */}
            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                Email Content (HTML) *
              </label>
              <textarea
                id="content"
                value={formData.content_html}
                onChange={(e) => handleHtmlContentChange(e.target.value)}
                rows={20}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 font-mono text-sm"
                placeholder={`<!DOCTYPE html>
<html>
<head>
    <title>{{campaign_name}}</title>
</head>
<body>
    <div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif;">
        <h1>Hello {{parent_name}}!</h1>
        
        <p>We hope this email finds you and {{child_name}} doing well.</p>
        
        <p>Your email content goes here...</p>
        
        <p>Best regards,<br>
        Your School Team</p>
    </div>
</body>
</html>`}
              />
              <div className="mt-2 text-sm text-gray-500">
                <p>Available personalization variables: {`{{parent_name}}, {{child_name}}, {{child_age}}, {{timeline}}, {{special_needs}}`}</p>
              </div>
            </div>
            </div>
          </div>

          {/* Right Column - Live Preview */}
          <div className="flex-1 lg:w-1/2 p-6 bg-gray-50 overflow-y-auto">
            <div className="sticky top-0 bg-gray-50 pb-4 mb-4 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-medium text-gray-900">Live Preview</h4>
                  <p className="text-sm text-gray-600">See how your email will look to recipients</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setPreviewMode('preview')}
                    className={`px-3 py-1 text-sm rounded-md ${
                      previewMode === 'preview' 
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewMode('source')}
                    className={`px-3 py-1 text-sm rounded-md ${
                      previewMode === 'source' 
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    Source
                  </button>
                </div>
              </div>
            </div>
            
            {formData.content_html ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                {/* Email Header Simulation */}
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <div className="text-sm text-gray-500 mb-2">Subject:</div>
                  <div className="text-lg font-medium text-gray-900">
                    {formData.subject || 'Your email subject will appear here'}
                  </div>
                </div>
                
                {/* Email Content */}
                {previewMode === 'preview' ? (
                  <>
                    {/* Default email styles for proper rendering */}
                    <style dangerouslySetInnerHTML={{ __html: `
                      .email-preview-content {
                        font-family: Arial, sans-serif;
                        line-height: 1.6;
                        color: #333;
                      }
                      .email-preview-content h1 {
                        font-size: 28px;
                        font-weight: bold;
                        margin: 20px 0 15px 0;
                        color: #111;
                      }
                      .email-preview-content h2 {
                        font-size: 22px;
                        font-weight: bold;
                        margin: 18px 0 12px 0;
                        color: #222;
                      }
                      .email-preview-content h3 {
                        font-size: 18px;
                        font-weight: bold;
                        margin: 15px 0 10px 0;
                        color: #333;
                      }
                      .email-preview-content p {
                        margin: 10px 0;
                        line-height: 1.6;
                      }
                      .email-preview-content ul, .email-preview-content ol {
                        margin: 10px 0;
                        padding-left: 30px;
                      }
                      .email-preview-content li {
                        margin: 5px 0;
                      }
                      .email-preview-content strong {
                        font-weight: bold;
                      }
                      .email-preview-content em {
                        font-style: italic;
                      }
                      .email-preview-content a {
                        color: #0066cc;
                        text-decoration: underline;
                      }
                      .email-preview-content blockquote {
                        margin: 15px 0;
                        padding: 10px 20px;
                        border-left: 3px solid #ddd;
                        background: #f9f9f9;
                      }
                      ${extractStyles(formData.content_html)}
                    ` }} />
                    
                    {/* Render the body content */}
                    <div 
                      className="email-preview-content"
                      dangerouslySetInnerHTML={{ 
                        __html: (() => {
                          // Extract body content
                          let bodyContent = extractBodyContent(formData.content_html);
                          
                          // Replace personalization variables with highlighted sample data
                          // Support both {{variable}} and {$variable} formats
                          const sampleData: Record<string, string> = {
                            parent_name: 'Sarah Johnson',
                            name: 'Sarah Johnson',  // MailerLite format
                            first_name: 'Sarah',    // Alternative MailerLite format
                            child_name: 'Emma',
                            child_age: '8',
                            timeline: 'Next semester',
                            special_needs: 'Autism spectrum support',
                            email: 'sarah.johnson@email.com',
                            campaign_name: formData.name || 'Campaign Name',
                            school_name: 'Armenta Learning Academy',
                            unsubscribe: '#unsubscribe',
                            forward: '#forward'
                          };

                          // Replace {{variable}} format
                          bodyContent = bodyContent.replace(/\{\{(\w+)\}\}/g, (match: string, key: string) => {
                            return `<span style="background-color: #fef3c7; padding: 2px 4px; border-radius: 4px; font-weight: 600;">${sampleData[key] || match}</span>`;
                          });

                          // Replace {$variable} format (MailerLite)
                          bodyContent = bodyContent.replace(/\{\$(\w+)\}/g, (match: string, key: string) => {
                            return `<span style="background-color: #e0f2fe; padding: 2px 4px; border-radius: 4px; font-weight: 600;">${sampleData[key] || match}</span>`;
                          });
                          
                          return bodyContent;
                        })()
                      }}
                    />
                    
                    {/* Check if full HTML document was provided */}
                    {(formData.content_html.includes('<body') || formData.content_html.includes('<!DOCTYPE')) && (
                      <div className="mt-4 text-xs text-gray-500 border-t pt-2">
                        ℹ️ Rendering body content from full HTML document
                      </div>
                    )}
                  </>
                ) : (
                  /* Source mode - show raw HTML */
                  <div className="bg-gray-900 rounded-md p-4 overflow-x-auto">
                    <pre className="text-sm text-gray-300 font-mono whitespace-pre-wrap break-words">
                      {formData.content_html}
                    </pre>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <div className="text-gray-400 text-lg mb-2">📧</div>
                <div className="text-gray-500 text-sm">
                  Start typing your email content to see the live preview
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between p-6 bg-gray-50 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            {selectedSegments.length > 0 && (
              <>
                {isEditing 
                  ? `Will be sent to ${totalRecipients} recipients`
                  : selectedSegments.length === 1
                    ? `Will create 1 campaign for ${totalRecipients} recipients`
                    : `Will create ${selectedSegments.length} campaigns for ${totalRecipients} total recipients`
                }
              </>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading || segments.length === 0 || selectedSegments.length === 0}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEditing ? 'Updating...' : selectedSegments.length === 1 ? 'Creating...' : 'Creating Campaigns...'}
                </>
              ) : (
                <>
                  {isEditing 
                    ? 'Update Campaign' 
                    : selectedSegments.length === 1
                      ? 'Create Campaign'
                      : `Create ${selectedSegments.length} Campaigns`
                  }
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignCreator;